/**
 * Lo que la ficha de un canal de la Cloud API lee de su `config`, que escribe el backend: la cuenta y el
 * número que dio Meta (D29), la desconexión y su motivo (D15), la salud (D30), los webhooks, y el método
 * de pago (D2). En puro, para probar cada motivo sin montar la ficha.
 */
import type { Channel } from "@/services/conversations.api";

/** WhatsApp Manager, donde el negocio carga el método de pago y ve la calidad del número. */
export const WHATSAPP_MANAGER_URL = "https://business.facebook.com/wa/manage/home/";

export const whatsappManagerDe = (wabaId?: string) =>
  wabaId ? `${WHATSAPP_MANAGER_URL}?waba_id=${encodeURIComponent(wabaId)}` : WHATSAPP_MANAGER_URL;

/** Por qué Meta desconectó el número, dicho para el admin del Store. */
const MOTIVOS_DE_DESCONEXION: Record<string, string> = {
  ACCOUNT_DISCONNECTED: "Se desconectó desde la app WhatsApp Business del celular.",
  BUSINESS_DOWNGRADE: "El número dejó de usar la app WhatsApp Business.",
  CHANGE_NUMBER: "Se le cambió el número a la cuenta desde la app WhatsApp Business.",
  COMPANION_INACTIVITY: "Meta cortó la conexión porque la integración estuvo unos 30 días sin actividad.",
  PRIMARY_INACTIVITY:
    "Meta cortó la conexión porque la app WhatsApp Business del celular estuvo unas dos semanas sin " +
    "usarse. Abrila en el celular y volvé a conectar.",
  USER_RE_REGISTERED: "El número se volvió a registrar en WhatsApp, en otro celular o en otra app.",
  PARTNER_REMOVED: "Meta desconectó el número de la integración.",
  PARTNER_APP_UNINSTALLED: "Se le sacó el acceso a xcons desde el portfolio de Meta del negocio.",
  ACCOUNT_OFFBOARDED:
    "El número se desconectó en la app WhatsApp Business, por ejemplo al cambiar de celular. Vuelve solo " +
    "cuando se reconecta desde la app.",
  ACCOUNT_DELETED: "La cuenta de WhatsApp Business se eliminó.",
  TOKEN_INVALID:
    "La autorización de Meta dejó de valer: se revocó, o se sacó la app del portfolio del negocio.",
  NUMBER_NOT_FOUND: "Meta ya no reconoce este número en la cuenta.",
};

/**
 * El motivo gana sobre el evento: un `PARTNER_REMOVED` dice poco, su `disconnection_info.reason` dice
 * qué pasó. En los motivos propios (`TOKEN_INVALID`, `NUMBER_NOT_FOUND`) el detalle es el texto de Meta,
 * en inglés, y se usa el evento.
 */
export function motivoDeDesconexion(d?: { event?: string | null; reason?: string | null } | null): string {
  const reason = d?.reason ?? "";
  const event = d?.event ?? "";
  return (
    MOTIVOS_DE_DESCONEXION[reason] ??
    MOTIVOS_DE_DESCONEXION[event] ??
    (reason || event ? `Meta desconectó el número (${reason || event}).` : "Meta desconectó el número.")
  );
}

const CALIDAD: Record<string, { texto: string; color: string }> = {
  GREEN: { texto: "Alta", color: "#52c41a" },
  YELLOW: { texto: "Media", color: "#faad14" },
  RED: { texto: "Baja", color: "#ff4d4f" },
};

export interface EstadoCloud {
  /** El onboarding terminó alguna vez: el canal tiene una cuenta de Meta. */
  conectadoAlgunaVez: boolean;
  wabaId?: string;
  numero?: string;
  nombreVerificado?: string;
  /** La calidad que Meta le da al número; `null` si todavía no la informó. */
  calidad: { texto: string; color: string } | null;
  /** Meta desconectó el número, con el motivo. */
  desconexion: { motivo: string; desde?: string } | null;
  /** Algo de los webhooks que el admin tiene que saber: el override falló o apuntan a otro lado. */
  avisoWebhook: string | null;
  /** El admin confirmó el método de pago de **esta** cuenta: otra cuenta en el mismo canal lo vuelve a pedir. */
  pagoConfirmado: boolean;
  /** Meta rechazó un mensaje por el método de pago, y nadie lo resolvió después. */
  problemaDePago: { desde?: string } | null;
  /** Cuándo el backend consultó la salud del número por última vez. */
  saludRevisada?: string;
}

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

export function estadoCloud(channel: Pick<Channel, "config" | "connectionStatus">): EstadoCloud {
  const config = obj(channel.config);
  const health = obj(config.health);
  const override = obj(config.webhook_override);
  const desconexion = config.disconnection ? obj(config.disconnection) : null;
  const confirmacion = obj(config.payment_confirmation);
  const wabaId = str(config.waba_id);
  const calidad = str(health.quality_rating) ?? str(config.quality_rating);

  const avisoWebhook =
    str(health.webhookWarning) ??
    (config.webhook_override && override.ok === false
      ? `No se pudo apuntar los mensajes de esta cuenta a este ambiente${
          str(override.error) ? ` (${override.error})` : ""
        }. Conectar con Facebook lo reintenta.`
      : null);

  return {
    conectadoAlgunaVez: Boolean(wabaId),
    wabaId,
    numero: str(config.display_phone_number),
    nombreVerificado: str(config.verified_name),
    calidad: calidad ? (CALIDAD[calidad] ?? null) : null,
    desconexion:
      desconexion && channel.connectionStatus === "disconnected"
        ? { motivo: motivoDeDesconexion(desconexion as { event?: string; reason?: string }), desde: str(desconexion.at) }
        : null,
    avisoWebhook,
    pagoConfirmado: Boolean(wabaId) && str(confirmacion.waba_id) === wabaId,
    problemaDePago: config.payment_issue ? { desde: str(obj(config.payment_issue).at) } : null,
    saludRevisada: str(health.checkedAt),
  };
}

/** Cómo va una parte del sync inicial, para la ficha: `ok`, esperando, o con un problema. */
export interface ParteDelSync {
  texto: string;
  tono: "ok" | "espera" | "problema";
  /** Del historial, de 0 a 100, mientras se importa. */
  progreso?: number;
}

export interface EstadoDelSync {
  agenda: ParteDelSync;
  historial: ParteDelSync;
  /** Hasta cuándo el servicio sigue intentando pedir lo que falló (D23). */
  reintentaHasta?: string;
  /** El último error de Meta al pedirlo, tal cual. */
  error?: string;
  /** La línea de la ficha, o `null` si ya terminó bien (#519). */
  resumen: ParteDelSync | null;
}

const fecha = (iso?: string) => (iso ? new Date(iso) : null);
const formato = (d: Date | null) =>
  d
    ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ` +
      `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    : "";

/**
 * El sync inicial de la agenda y el historial del celular (D16, D23), leído de `config.sync`. `null` si
 * el canal no tiene: nunca se conectó, o se conectó antes de que existiera. Lo maneja el servicio solo;
 * la ficha sólo lo muestra.
 */
export function estadoDelSync(config: Record<string, unknown> | undefined): EstadoDelSync | null {
  const sync = obj(config?.sync);
  if (!Object.keys(sync).length) return null;
  const dias = typeof sync.days === "number" ? sync.days : 60;
  const progreso = typeof sync.progress === "number" ? sync.progress : 0;
  const pedido = fecha(str(sync.requested_at));
  const esperaHasta = pedido ? new Date(pedido.getTime() + 24 * 60 * 60 * 1000) : null;

  const agenda: ParteDelSync = (() => {
    switch (sync.contacts) {
      case "done":
        return { texto: "Agenda del celular importada", tono: "ok" };
      case "requested":
        return { texto: "Esperando la agenda del celular", tono: "espera" };
      case "error":
        return { texto: "No se pudo pedir la agenda del celular", tono: "problema" };
      default:
        return { texto: "Pidiendo la agenda del celular", tono: "espera" };
    }
  })();

  const historial: ParteDelSync = (() => {
    switch (sync.history) {
      case "done":
        return { texto: `Historial importado: los chats de los últimos ${dias} días`, tono: "ok" };
      case "in_progress":
        return { texto: `Importando el historial de los últimos ${dias} días`, tono: "espera", progreso };
      case "requested":
        return {
          texto: `Esperando que Meta mande el historial${esperaHasta ? ` (hasta el ${formato(esperaHasta)})` : ""}`,
          tono: "espera",
        };
      case "declined":
        return {
          texto: "El historial no se compartió: se desactivó en la app WhatsApp Business al conectar el número",
          tono: "problema",
        };
      case "not_received":
        return {
          texto: "Meta aceptó el pedido pero no entregó el historial. Los mensajes nuevos entran igual",
          tono: "problema",
        };
      case "error":
        return { texto: "No se pudo pedir el historial", tono: "problema" };
      default:
        return { texto: "Pidiendo el historial", tono: "espera" };
    }
  })();

  const reintenta = [sync.contacts, sync.history].some((e) => e === "error" || e === "pending") && str(sync.next_attempt_at);
  const reintentaHasta = reintenta ? formato(fecha(str(sync.deadline_at))) : undefined;
  return {
    agenda,
    historial,
    reintentaHasta,
    error: str(sync.error),
    resumen: resumenDelSync(agenda, historial, sync.history, reintentaHasta),
  };
}

/**
 * La fila de la ficha (#519): una sola línea para agenda + historial, o `null` cuando los dos
 * terminaron bien — pasado eso ya no le dice nada a nadie. El error crudo de Meta no va: queda en
 * `error` para soporte.
 */
function resumenDelSync(
  agenda: ParteDelSync,
  historial: ParteDelSync,
  estadoDelHistorial: unknown,
  reintentaHasta: string | undefined,
): ParteDelSync | null {
  const partes = [agenda, historial];
  if (partes.every((p) => p.tono === "ok")) return null;
  if (partes.some((p) => p.tono === "problema")) {
    if (reintentaHasta) return { texto: `No se pudo importar todavía. Se reintenta solo hasta el ${reintentaHasta}`, tono: "problema" };
    // Lo que ya no se reintenta: el historial explica su motivo; si fue la agenda, se dice cuál.
    if (estadoDelHistorial === "declined" || estadoDelHistorial === "not_received") return historial;
    const queFalto = historial.tono === "problema" ? "el historial" : "la agenda del celular";
    return { texto: `No se importó ${queFalto}. Los mensajes nuevos entran igual`, tono: "problema" };
  }
  // Todavía en camino: el historial es lo que más tarda y el único con avance.
  return historial.tono === "espera" ? historial : agenda;
}
